import { useState, useRef, useEffect } from "react";
import { Formik } from "formik";
import { object } from "yup";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { captureException } from "@sentry/nextjs";
import { FunnelTextField } from "../../FunnelTextField";
import { FunnelButton } from "../../FunnelButton";
import { ValidatedFormikField } from "@portal/app/_components/ui-elements/formHelpers";
import { useAuthenticatedContext } from "@portal/app/_components/context/AuthenticatedContext";
import { ClientFacingErrors } from "@portal/app/_common/types/clientErrors";
import { detectKnownClientError } from "@portal/app/_common/lib/clientErrorUtils";
import { logger } from "@portal/app/_common/lib/logger";
import { yupRules } from "@portal/app/_common/lib/yupValidationRules";
import { SignInInsteadDialog } from "@portal/app/login/_components/SignInInsteadDialog";
import { useFormFunnelTracking } from "../../hooks/useFormFunnelTracking";
import {
  useEmailTypoWarning,
  EMAIL_MISSPELLED_ERROR,
} from "@portal/app/_components/hooks/useEmailTypoWarning";
import { EmailTypoWarning } from "@portal/app/_components/ui-elements/EmailTypoWarning";
import { QuestionDescription } from "../../QuestionDescription";
import { ContactStepProps } from "./types";
import { FunnelBackButton } from "../../FunnelBackButton";
import { useHostedStepAction } from "./useHostedStepAction";
import { useFormFunnelHeadingLevel } from "../../FormFunnelHeadingContext";

const EmailFieldWithTypoCheck = () => {
  const { onBlur, isMisspelled, warningSx } = useEmailTypoWarning();
  return (
    <>
      <ValidatedFormikField
        name="email"
        label="Email"
        as={FunnelTextField}
        type="email"
        fullWidth
        autoComplete="email"
        slotProps={{ htmlInput: { inputMode: "email" } }}
        onBlur={onBlur}
        sx={{ mb: isMisspelled ? 0 : 2, ...warningSx }}
      />
      {isMisspelled && (
        <Box sx={{ width: "100%", mb: 2 }}>
          <EmailTypoWarning />
        </Box>
      )}
    </>
  );
};

export const ContactStep = ({
  user,
  loggedIn,
  viewerData,
  customerDataRef,
  updateCustomer,
  createCustomer,
  refetchViewer,
  onNext,
  onBack,
  description,
}: ContactStepProps) => {
  const headingLevel = useFormFunnelHeadingLevel();
  const descriptionText = description?.content ? (
    <Box sx={{ my: 1 }}>
      <QuestionDescription content={description.content} testId="hosted-contact-step-description" />
    </Box>
  ) : null;
  const { openSignInModal } = useAuthenticatedContext();
  const { trackEvent } = useFormFunnelTracking();
  const [signInInsteadDialogOpen, setSignInInsteadDialogOpen] = useState(false);
  const pendingValuesRef = useRef<{ nameFirst: string; nameLast: string; email: string } | null>(
    null
  );
  const autoSubmittingRef = useRef(false);
  const action = useHostedStepAction();

  useEffect(() => {
    if (loggedIn && pendingValuesRef.current && !autoSubmittingRef.current) {
      const generation = action.begin();
      if (generation === undefined) return;
      autoSubmittingRef.current = true;
      const values = pendingValuesRef.current;
      void (async () => {
        try {
          await updateCustomer({
            variables: {
              data: {
                nameFirst: values.nameFirst,
                nameLast: values.nameLast,
                email: values.email,
              },
            },
          });
          await refetchViewer();
          if (!action.complete(generation)) return;
          pendingValuesRef.current = null;
          autoSubmittingRef.current = false;
          onNext();
        } catch (e) {
          const clientError = detectKnownClientError(e);
          if (clientError === ClientFacingErrors.UserWithEmailAlreadyExists) {
            autoSubmittingRef.current = false;
            setSignInInsteadDialogOpen(true);
            return;
          }
          logger.error(e);
          captureException(e);
          pendingValuesRef.current = null;
          autoSubmittingRef.current = false;
        } finally {
          action.finish(generation);
        }
      })();
    }
  }, [loggedIn, updateCustomer, refetchViewer, onNext, action]);

  return (
    <>
      <Formik
        initialValues={{
          nameFirst: user?.nameFirst || "",
          nameLast: user?.nameLast || "",
          email: user?.email || "",
        }}
        validationSchema={object({
          nameFirst: yupRules.required.nameFirst,
          nameLast: yupRules.required.nameLast,
          email: yupRules.required.email,
        })}
        onSubmit={async (values, { setSubmitting }) => {
          const generation = action.begin();
          if (generation === undefined) return;
          try {
            // Save customer data for final submission
            customerDataRef.current = {
              ...customerDataRef.current,
              nameFirst: values.nameFirst,
              nameLast: values.nameLast,
              email: values.email,
            };

            if (loggedIn) {
              await updateCustomer({
                variables: {
                  data: {
                    nameFirst: values.nameFirst,
                    nameLast: values.nameLast,
                    email: values.email,
                  },
                },
              });
            } else {
              await createCustomer({
                variables: {
                  data: {
                    nameFirst: values.nameFirst,
                    nameLast: values.nameLast,
                    email: values.email,
                    accountCreationFunnel: "FORM_FUNNEL",
                  },
                },
              });
            }
            await refetchViewer();
            if (!action.complete(generation)) return;
            trackEvent("Form Funnel - Hosted Step Completed", { stepType: "contact" });
            onNext();
          } catch (e) {
            const clientError = detectKnownClientError(e);
            if (
              clientError === ClientFacingErrors.UserWithEmailAlreadyExists ||
              clientError === ClientFacingErrors.UserWithPhoneAlreadyExists
            ) {
              pendingValuesRef.current = {
                nameFirst: values.nameFirst,
                nameLast: values.nameLast,
                email: values.email,
              };
              setSignInInsteadDialogOpen(true);
              return;
            }
            logger.error(e);
            captureException(e);
          } finally {
            action.finish(generation);
            setSubmitting(false);
          }
        }}
      >
        {({ handleSubmit, isSubmitting, isValid, dirty, errors }) => {
          const onlyTypoWarning =
            Object.keys(errors).length === 1 && errors.email === EMAIL_MISSPELLED_ERROR;

          return (
            <form noValidate onSubmit={handleSubmit} data-testid="hosted-contact-step">
              {onBack && (
                <FunnelBackButton appearance="text" onClick={onBack} sx={{ mb: 1 }}>
                  Back
                </FunnelBackButton>
              )}
              <Typography
                component={headingLevel}
                sx={{
                  color: "text.primary",
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  lineHeight: 1.2,
                  letterSpacing: "-0.01em",
                  mb: 3,
                }}
              >
                Where should we send your program details?
              </Typography>
              {description?.placement === "above" && descriptionText}
              <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
                <ValidatedFormikField
                  name="nameFirst"
                  label="First name"
                  as={FunnelTextField}
                  fullWidth
                  autoComplete="given-name"
                />
                <ValidatedFormikField
                  name="nameLast"
                  label="Last name"
                  as={FunnelTextField}
                  fullWidth
                  autoComplete="family-name"
                />
              </Box>
              <EmailFieldWithTypoCheck />
              {description?.placement !== "above" && descriptionText}
              <FunnelButton
                type="submit"
                disabled={isSubmitting || (!isValid && !onlyTypoWarning) || (!dirty && !viewerData)}
                sx={{ maxWidth: "none", minHeight: 56, mt: 1, fontSize: "1.1rem" }}
              >
                Continue
              </FunnelButton>
            </form>
          );
        }}
      </Formik>
      <SignInInsteadDialog
        onClose={(shouldOpenSignIn) => {
          setSignInInsteadDialogOpen(false);
          if (shouldOpenSignIn) {
            openSignInModal("SIGN_IN");
          } else {
            pendingValuesRef.current = null;
          }
        }}
        open={signInInsteadDialogOpen}
        isDreamSearch
      />
    </>
  );
};
